'use client'

import { usePathname } from 'next/navigation'
import HomeLoadingSkeleton from '@/components/skeletons/HomeLoadingSkeleton'
import CategoryLoadingSkeleton from '@/components/skeletons/CategoryLoadingSkeleton'
import {
  ArticlePageSkeleton,
  GridListPageSkeleton,
  ListPageSkeleton,
  SearchLandingSkeleton,
  SearchPageSkeleton,
  WeatherPageSkeleton,
} from '@/components/PageSkeletons'
import { SITE_CONTAINER, SITE_MAIN_PAD_Y, SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const bar = 'animate-pulse rounded-md bg-gray-200 dark:bg-slate-700'

/** Bilinen tek segment sabit sayfalar (kategori değil). */
const STATIC_TOP = new Set([
  'kunye',
  'iletisim',
  'reklam',
  'gizlilik-politikasi',
  'cerez-politikasi',
  'kullanim-kosullari',
  'kvkk',
  'rss-adresleri',
  'haber-gonder',
  'icerik-gonder',
  'authors',
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'account',
  'discover',
  'editor-x',
])

function GenericPageSkeleton() {
  return (
    <div
      className={SITE_PAGE_BG}
      aria-busy="true"
      aria-label="Sayfa yükleniyor"
    >
      <main className={cn(SITE_CONTAINER, SITE_MAIN_PAD_Y, 'space-y-4')}>
        <div className={cn(bar, 'h-8 w-56 max-w-[80%]')} />
        <div className={cn(bar, 'h-4 w-72 max-w-full')} />
        <div className="space-y-2.5 pt-4">
          <div className={cn(bar, 'h-3.5 w-full')} />
          <div className={cn(bar, 'h-3.5 w-[94%]')} />
          <div className={cn(bar, 'h-3.5 w-[88%]')} />
          <div className={cn(bar, 'h-3.5 w-full')} />
          <div className={cn(bar, 'h-3.5 w-[70%]')} />
        </div>
      </main>
    </div>
  )
}

function LiveTvHubSkeleton() {
  return (
    <div
      className={cn(SITE_PAGE_BG, 'pb-10')}
      aria-busy="true"
      aria-label="Canlı TV yükleniyor"
    >
      <main className={cn(SITE_CONTAINER, SITE_MAIN_PAD_Y, 'space-y-4')}>
        <div className={cn(bar, 'h-6 w-36')} />
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {Array.from({ length: 12 }, (_, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-2 rounded-[5px] border border-gray-200 p-3 dark:border-slate-700"
            >
              <div className={cn(bar, 'h-12 w-12 rounded-full')} />
              <div className={cn(bar, 'h-3.5 w-16')} />
              <div className={cn(bar, 'h-2.5 w-12')} />
            </div>
          ))}
        </div>
      </main>
    </div>
  )
}

function LiveTvWatchSkeleton() {
  return (
    <div
      className={cn(SITE_PAGE_BG, 'pb-10')}
      aria-busy="true"
      aria-label="Canlı yayın yükleniyor"
    >
      <main className={cn(SITE_CONTAINER, SITE_MAIN_PAD_Y, 'space-y-3')}>
        <div className="flex items-center gap-2.5 rounded-[5px] border border-gray-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950 sm:px-4">
          <div className={cn(bar, 'h-7 w-16')} />
          <div className={cn(bar, 'h-8 w-8 rounded-full')} />
          <div className={cn(bar, 'h-5 w-48 max-w-[50%]')} />
        </div>
        <div className={cn(bar, 'aspect-video w-full rounded-[5px]')} />
      </main>
    </div>
  )
}

function LiveScoresSkeleton() {
  return (
    <div
      className={cn(SITE_PAGE_BG, 'min-h-[50vh]')}
      aria-busy="true"
      aria-label="Canlı maçlar yükleniyor"
      role="status"
    >
      <main className={cn(SITE_CONTAINER, SITE_MAIN_PAD_Y)}>
        <div className="mb-3 grid grid-cols-3 border-b border-gray-200 dark:border-slate-800 sm:mb-5 sm:flex sm:gap-1">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex justify-center px-1.5 py-2.5 sm:px-3">
              <div className={cn(bar, 'h-4 w-20 sm:w-24')} />
            </div>
          ))}
        </div>

        <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 dark:border-slate-800 sm:mb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <div className={cn(bar, 'h-7 w-40 sm:h-8')} />
            <div className={cn(bar, 'h-3.5 w-72 max-w-[85vw]')} />
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <div className={cn(bar, 'h-9 w-10')} />
            <div className={cn(bar, 'h-9 flex-1 sm:w-36 sm:flex-none')} />
            <div className={cn(bar, 'h-9 w-10')} />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-5">
          <aside className="order-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-none lg:order-1 lg:self-start">
            <div className={cn(bar, 'mb-3 h-4 w-24')} />
            <div className="space-y-1.5">
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="flex h-10 items-center gap-2.5 rounded-lg px-2.5">
                  <div className={cn(bar, 'size-[22px] shrink-0 rounded-full')} />
                  <div className={cn(bar, i % 3 === 0 ? 'h-3.5 w-28' : 'h-3.5 w-24')} />
                  <div className={cn(bar, 'ml-auto h-3 w-5 rounded-full')} />
                </div>
              ))}
            </div>
          </aside>

          <div className="order-1 min-w-0 space-y-3 sm:space-y-4 lg:order-2">
            <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900 sm:hidden">
              <div className={cn(bar, 'size-[30px] shrink-0 rounded-full')} />
              <div className={cn(bar, 'h-4 w-28')} />
              <div className={cn(bar, 'ml-auto h-6 w-14 rounded-full')} />
            </div>

            <div className="hidden flex-col items-center sm:flex">
              <div className={cn(bar, 'size-[72px] rounded-full')} />
              <div className={cn(bar, 'mt-2.5 h-5 w-36')} />
              <div className={cn(bar, 'mt-2 h-3.5 w-20')} />
            </div>

            <div className="grid grid-cols-4 gap-1.5 sm:flex sm:gap-2">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className={cn(bar, 'h-8 min-w-0 rounded-lg sm:w-20')} />
              ))}
            </div>

            <div className="space-y-3">
              {Array.from({ length: 2 }, (_, sectionIndex) => (
                <div
                  key={sectionIndex}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
                >
                  <div className="flex h-11 items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className={cn(bar, 'size-5 rounded-full')} />
                    <div className={cn(bar, 'h-3.5 w-32')} />
                    <div className={cn(bar, 'h-3 w-4')} />
                    <div className={cn(bar, 'ml-auto h-3 w-6')} />
                  </div>
                  {Array.from({ length: sectionIndex === 0 ? 3 : 2 }, (_, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex h-[70px] items-center gap-3 border-b border-gray-100 px-3 last:border-0 dark:border-slate-800 sm:px-4"
                    >
                      <div className="w-16 shrink-0 space-y-1.5">
                        <div className={cn(bar, 'h-3.5 w-10')} />
                        <div className={cn(bar, 'h-4 w-14')} />
                      </div>
                      <div className={cn(bar, 'h-3.5 flex-1')} />
                      <div className={cn(bar, 'size-6 shrink-0 rounded-full')} />
                      <div className={cn(bar, 'h-7 w-14 shrink-0')} />
                      <div className={cn(bar, 'size-6 shrink-0 rounded-full')} />
                      <div className={cn(bar, 'hidden h-3.5 flex-1 sm:block')} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

function AdminPageSkeleton() {
  return (
    <div
      className="min-h-screen bg-gray-50 p-4 dark:bg-slate-950 sm:p-6"
      aria-busy="true"
      aria-label="Yönetim paneli yükleniyor"
    >
      <div className="mx-auto max-w-6xl space-y-4">
        <div className={cn(bar, 'h-8 w-48')} />
        <div className={cn(bar, 'h-10 w-full max-w-md')} />
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0 dark:border-slate-800"
            >
              <div className={cn(bar, 'h-4 w-1/4')} />
              <div className={cn(bar, 'h-4 w-1/3')} />
              <div className={cn(bar, 'ml-auto h-4 w-16')} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Pathname’e göre skeleton — sayfa başına loading yazılmaz.
 * Client navigasyonda NavigationSkeletonGate hedef URL ile bunu çağırır.
 */
export function resolveRouteSkeleton(pathname: string | null) {
  const p = (pathname || '').trim()
  if (!p) return <GenericPageSkeleton />

  if (p === '/') return <HomeLoadingSkeleton />
  if (p.startsWith('/admin')) return <AdminPageSkeleton />
  if (p.startsWith('/hava-durumu')) return <WeatherPageSkeleton />
  if (p.startsWith('/spor/canli-maclar')) return <LiveScoresSkeleton />
  if (p === '/canli-tv' || p === '/canli-tv/') return <LiveTvHubSkeleton />
  if (p.startsWith('/canli-tv/')) return <LiveTvWatchSkeleton />
  if (p.startsWith('/son-dakika')) return <ListPageSkeleton />
  if (p === '/arama') return <SearchLandingSkeleton />
  if (p.startsWith('/arama/')) return <SearchPageSkeleton />
  if (
    p.startsWith('/video') ||
    p.startsWith('/galeri') ||
    p.startsWith('/en-cok-') ||
    p.startsWith('/shorts') ||
    p.startsWith('/haberleri/')
  ) {
    return <GridListPageSkeleton count={8} />
  }

  // Makale: /kategori/slug-123
  if (/^\/[^/]+\/.+-\d+\/?$/.test(p)) return <ArticlePageSkeleton />

  const seg = p.split('/').filter(Boolean)
  if (seg.length === 1) {
    if (STATIC_TOP.has(seg[0]!)) return <GenericPageSkeleton />
    return <CategoryLoadingSkeleton categorySlug={seg[0]} />
  }

  if (
    p.startsWith('/author/') ||
    p.startsWith('/authors') ||
    p.startsWith('/login') ||
    p.startsWith('/register') ||
    p.startsWith('/account') ||
    p.startsWith('/discover') ||
    p.startsWith('/haber-gonder') ||
    p.startsWith('/icerik-gonder') ||
    p.startsWith('/forgot-password') ||
    p.startsWith('/reset-password')
  ) {
    return <GenericPageSkeleton />
  }

  if (seg.length >= 1 && !STATIC_TOP.has(seg[0]!)) {
    return <CategoryLoadingSkeleton categorySlug={seg[0]} />
  }

  return <GenericPageSkeleton />
}

export default function RouteLoading() {
  const pathname = usePathname()
  return resolveRouteSkeleton(pathname)
}
